/**
 * `<EmptyState>` — central canvas welcome card.
 *
 * Multi-Add Phase 2 W4 — re-located per engineer feedback 2026-05-13.
 *
 * Renders in the room's main canvas (between the trays) when the
 * studio has zero video sources. Per PRD §14 frame `17434-14926`
 * the welcome card is centered in the canvas, NOT in the narrow
 * left tray. The original W4 agent modified
 * `video/components/card/placeholder.tsx`, which renders in
 * `room/components/video/sources/list.tsx` (left tray) — wrong
 * location. This component is the proper landing surface; the
 * sidebar placeholder is removed by the same patch.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global):
 *
 *   - `ui-architecture` — Tailwind theme tokens are preferred. The
 *     icon color is the literal Figma value `#4E5966` (Cool-Grey-600)
 *     and is NOT in the design-system palette as a named token, so it
 *     ships as a Tailwind arbitrary value (`text-[#4E5966]`). Hive's
 *     muted token resolves elsewhere; the empty-state icon is pinned
 *     to the Iris reference by the design (engineer note 2026-05-13).
 *   - SINGULAR copy throughout per engineer override 2026-05-13:
 *     - Heading: "Welcome to Iris, let's get started"
 *     - Sub-copy: "Add your first video source to start exploring
 *       your Iris studio"
 *     - CTA: "Add a source" (singular, NOT "Add Sources")
 *   - `stay-in-scope` — onClick is wired via `useAddVideo()` (same
 *     hook the left-tray `+` button uses), so the CTA opens the
 *     standard Add Sources modal.
 */
import { useAddVideo } from '#~/room/components/video/hooks.js';
import { Button } from '@studio/design-system/button';
import type { ReactElement } from 'react';

/**
 * Inline SVG for the empty-state welcome icon. Sourced from the Figma
 * reference frame `17434-14926` ("Union.svg") — a camera body with a
 * `+` overlay in the center (96×67 viewBox). The design-system
 * iconfont doesn't ship this specific glyph, so it lives inline here.
 *
 * Per `ui-architecture` (ai.knowledge/global): `fill='currentColor'`
 * so a Tailwind text utility on the parent drives the color. The
 * specific hex `#4E5966` is the Figma value (Cool-Grey-600) and is
 * applied as an arbitrary value at the call site below — the iris
 * `muted` palette token resolves to `#a9b3c6`, which is the wrong
 * shade per the design reference.
 */
const WelcomeCameraIcon = ({ className }: { className?: string }): ReactElement => (
  <svg
    className={className}
    width='96'
    height='67'
    viewBox='0 0 96 67'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M49.7002 0C53.5668 -6.7061e-10 56.5271 -0.000621415 58.8896 0.192383C61.2638 0.386359 63.1229 0.780652 64.7705 1.62012C67.5678 3.04541 69.8422 5.31995 71.2676 8.11719C72.1071 9.76482 72.5023 11.6239 72.6963 13.998C72.8893 16.3606 72.8887 19.3208 72.8887 23.1875V30.9424L87.9805 15.8506C88.9566 14.8745 89.7364 14.0934 90.3799 13.5547C90.998 13.0372 91.668 12.5834 92.4297 12.5234C93.4862 12.4403 94.5187 12.868 95.207 13.6738C95.7032 14.2548 95.8566 15.0496 95.9277 15.8525C96.0018 16.6885 96.001 17.7925 96.001 19.1729V47.541C96.001 48.9214 96.0018 50.0254 95.9277 50.8613C95.8566 51.6641 95.7031 52.4582 95.207 53.0391C94.5187 53.845 93.4863 54.2735 92.4297 54.1904C91.6681 54.1305 90.998 53.6766 90.3799 53.1592C89.7364 52.6205 88.9566 51.8393 87.9805 50.8633L72.8887 35.7705V43.5264C72.8887 47.3929 72.8893 50.3533 72.6963 52.7158C72.5023 55.0898 72.107 56.9482 71.2676 58.5957C69.8422 61.3931 67.5679 63.6674 64.7705 65.0928C63.1228 65.9323 61.2639 66.3275 58.8896 66.5215C56.5271 66.7145 53.5668 66.7139 49.7002 66.7139H23.1875C19.3209 66.7139 16.3606 66.7145 13.998 66.5215C11.624 66.3275 9.76572 65.9322 8.11816 65.0928C5.32069 63.6674 3.04551 61.3931 1.62012 58.5957C0.780751 56.9482 0.386366 55.0897 0.192383 52.7158C-0.000643021 50.3533 -4.09715e-08 47.3929 0 43.5264V23.1875C-2.30111e-09 19.3208 -0.000647383 16.3606 0.192383 13.998C0.38637 11.6239 0.7806 9.76481 1.62012 8.11719C3.04551 5.31984 5.32076 3.04545 8.11816 1.62012C9.76565 0.780796 11.6241 0.386354 13.998 0.192383C16.3606 -0.000639457 19.3209 -4.20549e-08 23.1875 0H49.7002ZM23.1875 2C19.2879 2 16.4233 2.00072 14.1611 2.18555C11.9106 2.36942 10.3441 2.73043 9.02539 3.40234C6.60438 4.63595 4.63595 6.6044 3.40234 9.02539C2.73042 10.3441 2.36943 11.9106 2.18555 14.1611C2.00073 16.4233 2 19.2879 2 23.1875V43.5264C2 47.4259 2.00072 50.2906 2.18555 52.5527C2.36941 54.803 2.73057 56.3689 3.40234 57.6875C4.63596 60.1086 6.60428 62.0779 9.02539 63.3115C10.3441 63.9834 11.9106 64.3444 14.1611 64.5283C16.4233 64.7132 19.2879 64.7139 23.1875 64.7139H49.7002C53.5999 64.7139 56.4653 64.7132 58.7275 64.5283C60.9777 64.3444 62.5437 63.9833 63.8623 63.3115C66.2835 62.0779 68.2517 60.1086 69.4854 57.6875C70.1572 56.3689 70.5192 54.8031 70.7031 52.5527C70.8879 50.2906 70.8887 47.4259 70.8887 43.5264V23.1875C70.8887 19.2879 70.8879 16.4233 70.7031 14.1611C70.5192 11.9106 70.1573 10.3441 69.4854 9.02539C68.2517 6.60444 66.2833 4.63591 63.8623 3.40234C62.5437 2.73055 60.9777 2.36942 58.7275 2.18555C56.4653 2.00072 53.5999 2 49.7002 2H23.1875ZM92.5869 14.5176C92.4986 14.5245 92.2482 14.599 91.6641 15.0879C91.105 15.556 90.3986 16.2606 89.3945 17.2646L73.3018 33.3564L89.3945 49.4492C90.3986 50.4533 91.105 51.1569 91.6641 51.625C92.2484 52.1142 92.4986 52.1893 92.5869 52.1963C93.0052 52.2291 93.414 52.0593 93.6865 51.7402C93.7442 51.6725 93.8684 51.4426 93.9355 50.6846C93.9999 49.9582 94.001 48.961 94.001 47.541V19.1729C94.001 17.753 93.9999 16.7556 93.9355 16.0293C93.8683 15.2701 93.7441 15.04 93.6865 14.9727C93.414 14.6537 93.0051 14.4848 92.5869 14.5176ZM36.7354 17.752C37.2875 17.7521 37.7354 18.1998 37.7354 18.752V31.8408H50.8242C51.3764 31.841 51.8242 32.2886 51.8242 32.8408C51.8241 33.3929 51.3763 33.8407 50.8242 33.8408H37.7354V46.9307C37.7351 47.4827 37.2874 47.9305 36.7354 47.9307C36.1833 47.9306 35.7356 47.4827 35.7354 46.9307V33.8408H22.6455C22.0933 33.8408 21.6456 33.393 21.6455 32.8408C21.6455 32.2885 22.0932 31.8408 22.6455 31.8408H35.7354V18.752C35.7354 18.1997 36.1831 17.752 36.7354 17.752Z'
      fill='currentColor'
    />
  </svg>
);

/**
 * Empty-canvas welcome card. Centered in the available space, no card
 * chrome (lives directly on the canvas surface).
 */
export const EmptyState = (): ReactElement => {
  const addVideo = useAddVideo();

  return (
    <div
      // Center in the canvas. `absolute inset-0` claims the full
      // canvas area; `flex items-center justify-center` centers
      // children both ways.
      className='absolute inset-0 flex flex-col items-center justify-center gap-16 text-center text-foreground'
      // a11y: this is the main empty-state landing for the room.
      role='region'
      aria-label='Empty studio welcome'
    >
      {/* Camera-with-plus glyph — Figma frame `17434-14926` reference.
          Not in the design-system iconfont (it's a Figma Union of
          multiple shapes); inlined as `WelcomeCameraIcon` above.
          Color is `#4E5966` (Cool-Grey-600) per Figma — applied as an
          arbitrary Tailwind value because the palette `muted` token
          resolves to `#a9b3c6` (the wrong shade for this surface). */}
      <WelcomeCameraIcon className='text-[#4E5966]' />

      {/* Heading + sub-copy live in an inner flex container with
          `gap-8` because Figma spec for frame `17434-14926` calls for
          8px between the two text blocks specifically, while the
          outer container's `gap-16` controls icon→text and
          text→button spacing. The h1 needs three overrides to actually
          land at 8px:
            1. `leading-100` neutralises the design-system's global
               `h1{line-height:1.3}` rule (otherwise 5.4px of inherited
               leading inflates the visible gap).
            2. `mb-0` zeroes out a stray `h1{margin-bottom:1rem}` from
               the base stylesheet that flex `gap` is additive to
               (without it the rendered box-to-box gap is 16+8=24px).
            3. The wrapping `gap-8` controls the remaining flex gap.
          Per `stay-in-scope` (ai.knowledge/global): one container
          override beats a global token rewrite. */}
      <div className='flex flex-col items-center gap-8'>
        {/* SINGULAR heading per engineer override 2026-05-13 —
            matches PRD §14 frame `17434-14926`. */}
        <h1 className='mb-0 text-lg font-demibold leading-100'>
          Welcome to Iris, let&apos;s get started
        </h1>

        {/* SINGULAR sub-copy per engineer override 2026-05-13 —
            matches PRD §14 frame `17434-15275`. */}
        <p className='text-sm text-muted'>
          Add your first video source to start exploring your Iris studio
        </p>
      </div>

      {/* CTA — first interactive element in the empty canvas; this
          is the first tab stop when the card mounts. Design-system
          `<Button>` renders a native `<button type='button'>`. */}
      <Button autoFocus onClick={addVideo} variant='primary' size='md'>
        Add a source
      </Button>
    </div>
  );
};

EmptyState.displayName = 'EmptyState';
